import { useEffect, useRef, useState } from "react";
import { Inbox, LifeBuoy, Paperclip, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatChatDate } from "@/lib/format";
import { useLanguage } from "@/i18n/LanguageContext";

const CATEGORIES = ["Question", "Facturation", "Technique", "Partenariat", "Autre"];

const fileUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const Attachment = ({ url, name }) => {
  const { t } = useLanguage();
  if (!url) return null;
  return (
    <a
      data-testid="message-attachment"
      href={fileUrl(url)}
      target="_blank"
      rel="noreferrer"
      className="mt-1.5 flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold underline-offset-2 hover:underline"
      style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
    >
      <Paperclip className="h-3 w-3" />
      {name || t("Pièce jointe")}
    </a>
  );
};

export const ContactAdminView = () => {
  const { t, lang } = useLanguage();
  const [convs, setConvs] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState("");
  const [firstMessage, setFirstMessage] = useState("");
  const [category, setCategory] = useState("Question");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInput = useRef(null);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro/uploads", fd);
      setAttachment({ url: data.url, name: data.name });
      toast.success(t("Pièce jointe prête"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const loadList = () =>
    api.get("/pro/conversations").then((r) => {
      setConvs(r.data);
      return r.data;
    }).catch(() => setConvs([]));

  const openConversation = (id) => {
    setActiveId(id);
    setCreating(false);
    api.get(`/pro/conversations/${id}`).then((r) => {
      setThread(r.data);
      loadList();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }).catch(() => toast.error(t("Impossible de charger la conversation")));
  };

  useEffect(() => {
    loadList().then((list) => {
      const alreadyRead = list?.find((c) => c.unread_pro === 0);
      if (alreadyRead) openConversation(alreadyRead.id);
      else if (!list?.length) setCreating(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!subject.trim() || !firstMessage.trim()) {
      toast.error(t("Ajoute un sujet et un message"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/pro/conversations", {
        subject,
        text: firstMessage,
        category,
        attachment_url: attachment?.url || null,
        attachment_name: attachment?.name || null,
      });
      toast.success(t("Message envoyé à l'équipe GoSpot"));
      setSubject("");
      setFirstMessage("");
      setCategory("Question");
      setAttachment(null);
      await loadList();
      openConversation(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    if ((!text.trim() && !attachment) || !activeId) return;
    setSending(true);
    try {
      await api.post(`/pro/conversations/${activeId}/messages`, {
        text,
        attachment_url: attachment?.url || null,
        attachment_name: attachment?.name || null,
      });
      setText("");
      setAttachment(null);
      openConversation(activeId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (convs === null) {
    return <div className="mt-6 h-96 animate-pulse rounded-3xl bg-white" />;
  }

  const active = convs.find((c) => c.id === activeId);

  return (
    <div className="mt-6 grid gap-5 lg:grid-cols-[340px_1fr]" data-testid="pro-contact-admin">
      {/* LISTE DES CONVERSATIONS */}
      <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <p className="text-sm font-bold text-slate-900">
            {t("Mes demandes")}
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{convs.length}</span>
          </p>
          <button
            type="button"
            data-testid="pro-new-conversation"
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 rounded-full bg-[#2563EB] px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#1D4ED8]"
          >
            <Plus className="h-3 w-3" />
            Nouveau
          </button>
        </div>
        <div className="max-h-[520px] divide-y divide-slate-50 overflow-y-auto" data-testid="pro-conv-list">
          {convs.length === 0 && (
            <p className="px-5 py-8 text-center text-xs text-slate-400">{t("Aucune demande pour le moment.")}</p>
          )}
          {convs.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`pro-conv-${c.id}`}
              onClick={() => openConversation(c.id)}
              className={`flex w-full items-start gap-3 px-4 py-3.5 text-left transition ${activeId === c.id && !creating ? "bg-[#EFF6FF]" : "hover:bg-slate-50"}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${activeId === c.id && !creating ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-400"}`}>
                <LifeBuoy className="h-4.5 w-4.5" size={18} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900">{c.subject}</span>
                  {c.unread_pro > 0 && (
                    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#2563EB] px-1 text-[10px] font-bold text-white" data-testid={`pro-conv-unread-${c.id}`}>
                      {c.unread_pro}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-400">{c.last_message?.text}</span>
                <span className="mt-0.5 block text-[10px] text-slate-300">{formatChatDate(c.updated_at, lang)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* PANNEAU DROIT : NOUVELLE DEMANDE OU FIL */}
      {creating ? (
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="pro-new-conv-form">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <LifeBuoy className="h-4 w-4 text-[#2563EB]" />
              Nouvelle demande à l'équipe GoSpot
            </h2>
            {convs.length > 0 && (
              <button type="button" onClick={() => setCreating(false)} aria-label={t("Fermer")} className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">{t("Une question sur ton forfait, ta fiche ou tes événements ? L'admin te répond ici.")}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input
              data-testid="pro-conv-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("Sujet (ex. : Question sur mon forfait)")}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
            />
            <select
              data-testid="pro-conv-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full cursor-pointer rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#2563EB]"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="pro-conv-message"
            rows={5}
            value={firstMessage}
            onChange={(e) => setFirstMessage(e.target.value)}
            placeholder={t("Décris ta demande…")}
            className="mt-3 w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
          />
          {attachment && (
            <p data-testid="pro-conv-attachment" className="mt-2 flex w-fit items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3 py-1.5 text-xs font-semibold text-[#2563EB]">
              <Paperclip className="h-3 w-3" />
              {attachment.name}
              <button type="button" aria-label={t("Retirer la pièce jointe")} onClick={() => setAttachment(null)} className="ml-1 rounded-full hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            </p>
          )}
          <div className="mt-4 flex items-center gap-2.5">
            <button
              type="button"
              data-testid="pro-conv-attach"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-60"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {uploading ? "Envoi…" : "Pièce jointe"}
            </button>
            <button
              type="button"
              data-testid="pro-conv-create"
              onClick={create}
              disabled={sending || uploading}
              className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Envoyer à l'admin
            </button>
          </div>
        </div>
      ) : active ? (
        <div className="flex min-h-[480px] flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm" data-testid="pro-thread">
          <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EFF6FF]">
              <LifeBuoy className="h-4.5 w-4.5 text-[#2563EB]" size={18} />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900">{active.subject}</p>
              <p className="flex items-center gap-2 text-xs text-slate-400">
                Support GoSpot • ouverte le {formatChatDate(active.created_at, lang)}
                <span className="rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[9px] font-bold text-[#2563EB]">{active.category || "Question"}</span>
              </p>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-5" data-testid="pro-thread-messages">
            {thread?.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === "pro" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[75%] rounded-3xl px-4 py-3 ${
                    m.sender_role === "pro"
                      ? "rounded-br-md bg-[#2563EB] text-white"
                      : "rounded-bl-md border border-slate-100 bg-white text-slate-800"
                  }`}
                >
                  <p className={`text-[10px] font-bold ${m.sender_role === "pro" ? "text-blue-200" : "text-slate-400"}`}>
                    {m.sender_role === "pro" ? "Toi" : `Admin GoSpot`} • {formatChatDate(m.created_at, lang)}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p>
                  <Attachment url={m.attachment_url} name={m.attachment_name} />
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-slate-100 p-4">
            {attachment && (
              <p data-testid="pro-reply-attachment" className="mb-2 flex w-fit items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3 py-1.5 text-xs font-semibold text-[#2563EB]">
                <Paperclip className="h-3 w-3" />
                {attachment.name}
                <button type="button" aria-label={t("Retirer la pièce jointe")} onClick={() => setAttachment(null)} className="ml-1 rounded-full hover:text-red-500">
                  <X className="h-3 w-3" />
                </button>
              </p>
            )}
            <div className="flex items-end gap-2.5">
              <button
                type="button"
                data-testid="pro-reply-attach"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                aria-label={t("Joindre un fichier")}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-50"
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <textarea
                data-testid="pro-reply-input"
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={t("Écris ta réponse à l'admin…")}
                className="min-w-0 flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
              />
              <button
                type="button"
                data-testid="pro-reply-send"
                onClick={send}
                disabled={sending || (!text.trim() && !attachment)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-white shadow-sm transition hover:bg-[#1D4ED8] disabled:opacity-40"
                aria-label={t("Envoyer")}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[480px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <div>
            <Inbox className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">{t("Sélectionne une demande ou crées-en une nouvelle.")}</p>
          </div>
        </div>
      )}
      <input ref={fileInput} type="file" accept="image/*,application/pdf" className="hidden" data-testid="pro-conv-file-input" onChange={uploadFile} />
    </div>
  );
};
